import React, { useEffect, useRef, useState } from 'react';
import PlanningServiceItemService from '../../../Services/PlanningServiceItemService.js';
import AuthService from '../../../Services/authService';
import ServiceItem from '../../../Domain/ServiceItem';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum.js';
import SLSearchParams from './SLSearchParams.js';
import searchStateStore from '../../../Helpers/searchStateStore';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import ServiceAccountManagerEditWindowDescription from './ServiceAccountManagerEditWindowDescription';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ButtonRow from '../../../Elements/ButtonRow';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import PlanningExportWindowDescription from '../Export/PlanningExportWindowDescription';
import PlanningExportItem from '../../../Domain/PlanningExportItem';
import PlanningExportLevelsEnum from '../../../Enums/PlanningExportLevelsEnum';

function ServiceList(props) {
  // Embedded in another page (an opportunity's services), rather than the Planning tab's own
  // list. Its filter is dictated by the host page, so it is neither remembered nor restored.
  const isEmbedded = !!(props.ServiceId || props.OpportunityId);

  // Same gate updateSearchParams uses: the URL is only read on a .../search path, and it has
  // to actually carry something.
  const urlCarriesAFilter = () =>
    window.location.pathname.split('/')[2] === 'search' && window.location.search.length > 1;

  const buildInitialSearchItem = () => {
    const searchItem = new SLSearchParams();
    if (isEmbedded) {
      searchItem.ServiceId = props.ServiceId || 0;
      searchItem.OpportunityId = props.OpportunityId || 0;
      searchItem.OfficeIds = [];
      return searchItem;
    }
    if (urlCarriesAFilter()) {
      return searchItem;
    }
    // Stepping back up from a sub-page re-mounts this list, so the filter the user left with
    // is put back instead of being rebuilt from defaults. A link that asks for something
    // specific still wins, above; only someone who has not searched at all this session
    // lands on their own office.
    const remembered = searchStateStore.recall(SLSearchParams.saveType);
    if (remembered) {
      return Object.assign(new SLSearchParams(true), remembered);
    }
    searchItem.OfficeIds = [AuthService.getInstance().GetLoggedInUser().officeId];
    return searchItem;
  };

  const [searchItem, setSearchItem] = useState(() => buildInitialSearchItem());
  const [shownPanel, setShownPanel] = useState(ShownPanelsEnum.None);
  const [accountManagerEditItem, setAccountManagerEditItem] = useState(null);
  const [serviceItemsList, setServiceItemsList] = useState([]);

  const listRef = useRef(serviceItemsList);
  listRef.current = serviceItemsList;

  const refresh = () => {
    setSearchItem((prev) => ({ ...prev }));
  };

  const closePanel = () => {
    setShownPanel(ShownPanelsEnum.None);
  };

  useEffect(() => {
    ServiceItem.openAccountManagerEditor = (item) => {
      setAccountManagerEditItem(item);
      setShownPanel(ShownPanelsEnum.ServiceAccountManagerEdit);
    };
    ServiceItem.clickOnSelect = (item) => {
      item.Selected = !item.Selected;
      setServiceItemsList([...listRef.current]);
    };
    ServiceItem.clickOnGlobalSelect = () => {
      const list = listRef.current;
      const newSelected = list.some((item) => item.Selected === false);
      const templist = [...list];
      templist.forEach((c) => {
        c.Selected = newSelected;
      });
      setServiceItemsList(templist);
    };
    ServiceItem.selectedStatus = () => !listRef.current.some((x) => x.Selected === false);
  }, []);

  const startSearch = (newSearchItem) => {
    if (!isEmbedded) {
      searchStateStore.remember(SLSearchParams.saveType, newSearchItem);
    }
    setSearchItem(newSearchItem);
  };

  const renderAccountManagerEditPanel = () => {
    if (!accountManagerEditItem) return null;
    return (
      <GeneralEditWizzardBase
        crudType={CrudTypesEnum.Create}
        editedItem={new ServiceAccountManagerEditWindowDescription.itemType(accountManagerEditItem)}
        windowDescription={ServiceAccountManagerEditWindowDescription}
        clickOnSave={() => {
          closePanel();
          refresh();
        }}
        clickOnCancel={() => closePanel()}
        errorCode={'PSA_EDIT'}
      />
    );
  };

  const renderButtonRow = () => (
    <ButtonRow
      colors={[ButtonColorsEnum.Secondary]}
      labels={['EXPORT']}
      keys={[ShownPanelsEnum.Export]}
      icons={['fa-solid fa-file-excel']}
      value={shownPanel}
      onClick={(key) => setShownPanel((prev) => (key === prev ? ShownPanelsEnum.None : key))}
      roleKeys={['user_key']}
      buttonClassNames={['']}
      disabled={[serviceItemsList.filter((x) => x.Selected).length === 0]}
    />
  );

  const renderExportPanel = () => (
    <GeneralEditWizzardBase
      crudType={CrudTypesEnum.Create}
      editedItem={
        new PlanningExportItem({
          Level: PlanningExportLevelsEnum.Service,
          SelectedIds: serviceItemsList.filter((x) => x.Selected).map((x) => x.VPlanningServiceItemId),
        })
      }
      windowDescription={PlanningExportWindowDescription.forLevel(PlanningExportLevelsEnum.Service)}
      clickOnSave={() => closePanel()}
      clickOnCancel={() => closePanel()}
      errorCode={'SL_EXPORT'}
    />
  );

  return (
    <div className='contentwrapper'>
      {renderButtonRow()}
      {shownPanel === ShownPanelsEnum.ServiceAccountManagerEdit && renderAccountManagerEditPanel()}
      {shownPanel === ShownPanelsEnum.Export && renderExportPanel()}
      <TabOfDataTable
        type={ServiceItem}
        shownPanel={shownPanel}
        serviceFunction={PlanningServiceItemService.GetServiceItems}
        convertDBItemsFunction={PlanningServiceItemService.ConvertDBServiceItems}
        searchItem={searchItem}
        orderDirection={OrderDirectionsEnum.Desc}
        orderBy='VPlanningServiceItemId'
        showSearchStrip={true}
        resizableColumns={true}
        searchParamsType={SLSearchParams}
        clickOnStartSearch={startSearch}
        singleSearchLine={true}
        singleSearchLinePlaceholder='Search in different ids and names...'
        itemList={serviceItemsList}
        changeOfItemListFunction={(itemList) => setServiceItemsList(itemList)}
        forceClearCacheOnSearch={true}
        errorCode='SL50'
      />
    </div>
  );
}

ServiceList.defaultProps = {
  ServiceId: 0,
  OpportunityId: 0,
};

export default ServiceList;
